<template>
  <div v-if="sections.length === 0">
    <div v-if="hasSectionSelectorApp && !isAdmin"></div>
    <div v-else class="flex flex-col items-center justify-center my-6 gap-4">
      <CIcon class="color-primary4" fa-type="fas" fixed-width icon="bring-forward" size="36" />

      <span class="color-primary3 font-size-[14px] line-height-[20px] tracking-[.25px]">
        {{ t('canvasUI.canvasBuilder.sectionListRaw.addSections') }}
      </span>

      <CButton :color="themeVars.primary" ghost :text-color="themeVars.primary" type="primary" @click="selectSections">
        {{ t('canvasUI.canvasBuilder.sectionListRaw.browseAll') }}
      </CButton>
    </div>
  </div>

  <div v-else class="p-2 flex flex-col w-full gap-4">
    <header class="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:items-end">
      <div class="w-full">
        <h2 class="font-600 font-size-[15px] line-height-[28px] tracking-[0.15px] color-text">
          {{ t('canvasUI.canvasBuilder.sectionListRaw.title', { quantity: sections.length }) }}
        </h2>

        <div class="font-size-[14px] line-height-[20px] tracking-[0.5px] color-primary3">
          {{ t('canvasUI.canvasBuilder.sectionListRaw.info') }}
        </div>
      </div>

      <div class="flex gap-2 w-full lg:justify-end">
        <Search
          v-model:search="search"
          class="w-full max-w-[300px] min-w-[150px]"
          :placeholder="t('canvasUI.canvasBuilder.sectionListRaw.searchPlaceholder')"
        />

        <CPopover
          class="max-w-[220px] max-h-[400px] overflow-y-auto"
          placement="bottom-end"
          :show-arrow="false"
          trigger="click"
        >
          <template #trigger>
            <NButton
              class="font-700 font-size-[14px] line-height-[1.2] tracking-[0.3px] transform-none bg-primary5"
              secondary
              strong
              :theme-overrides="{
                heightMedium: '32px',
              }"
            >
              <span>
                {{ t('canvasUI.canvasBuilder.sectionListRaw.filters.show') }}:
                <span class="font-500">{{ activeFilterName }}</span>
              </span>
            </NButton>
          </template>

          <NRadioGroup
            v-model:value="editabilityFilter"
            class="flex flex-col gap-2 line-height-[24px] tracking-[0.5px]"
          >
            <NRadio :theme-overrides="radioTheme('all')" value="all">
              {{ t('canvasUI.canvasBuilder.sectionListRaw.filters.all') }}
            </NRadio>

            <NRadio :theme-overrides="radioTheme('editable')" value="editable">
              {{ t('canvasUI.canvasBuilder.sectionListRaw.filters.editable') }}
            </NRadio>

            <NRadio :theme-overrides="radioTheme('noEditable')" value="noEditable">
              {{ t('canvasUI.canvasBuilder.sectionListRaw.filters.noEditable') }}
            </NRadio>
          </NRadioGroup>

          <template #footer>
            <NButton
              class="mx-auto w-full tracking-[0.5px] font-size-4"
              ghost
              :theme-overrides="{
                heightMedium: '32px',
              }"
              @click="editabilityFilter = 'all'"
            >
              {{ t('canvasUI.canvasBuilder.sectionListRaw.filters.clear') }}
            </NButton>
          </template>
        </CPopover>
      </div>
    </header>

    <div class="flex flex-col overflow-auto max-h-63vh min-h-[135px]">
      <DynamicScroller
        v-if="filteredSections.length"
        class="flex flex-col"
        :items="filteredSections"
        key-field="index"
        :min-item-size="135"
      >
        <template v-slot="{ item, item: { section, index }, index: loopIndex, active }">
          <DynamicScrollerItem :active="active" :data-index="loopIndex" :item="item">
            <div
              class="p-4 flex gap-3 border-t-solid border-t-[1px]"
              :class="loopIndex === 0 ? 'border-transparent' : 'border-primary5'"
              :data-section-component-id="section.id"
              :data-section-index="index"
            >
              <div class="flex gap-3 items-stretch flex-shrink-1" :class="{ 'opacity-50': section.is_draft }">
                <CThumbnail
                  :height="100"
                  :iconSize="24"
                  image-class="rounded-1 bg-primary6 justify-center items-center"
                  :item="section"
                  :preview-disabled="false"
                  :themeVars="themeVars"
                  :thumbnail-url="section.thumbnail_url ?? section.metadata?.thumbnail"
                  :width="152"
                  @click.stop
                />
              </div>

              <div class="flex flex-col gap-3 flex-auto justify-center overflow-hidden">
                <h3
                  class="font-700 font-size-[18px] tracking-[0.4px] line-height-[1.2]"
                  :class="{ 'opacity-50': section.is_draft }"
                >
                  {{ section.name }}
                </h3>

                <div
                  class="flex gap-x-2 gap-y-1 flex-wrap overflow-auto [&:empty]:hidden"
                  :class="{ 'opacity-50': section.is_draft }"
                >
                  <CMetaDataBadge
                    v-for="(meta, key) in metadataMap.get(section.id)"
                    :key="key"
                    :label="meta.label"
                    :value="meta.value"
                  />
                </div>

                <div class="flex flex-wrap gap-x-2 gap-y-1 items-center [&:empty]:hidden">
                  <!-- Draft Indicator -->
                  <CInfoBadge
                    v-if="section.is_draft"
                    v-tooltip="t('canvasUI.canvasBuilder.sectionListRaw.status.draftTooltip')"
                    :background-color="themeVars.text2"
                    :color="themeVars.base"
                    icon="eye-slash"
                    :text="t('canvasUI.canvasBuilder.sectionListRaw.status.draft')"
                  />

                  <!-- Popularity and Engagement -->
                  <CPopularityAndEngagementExtended
                    v-if="Object.keys(popularityDict)?.length"
                    class="gap-x-2 gap-y-1 contents!"
                    :engagement="
                      launchDarkly.show_popularity_icons ? popularityDict[section.id]?.engagement : undefined
                    "
                    :is-recommended="recommendedSectionIds.includes(section.id)"
                    :popularity="
                      launchDarkly.show_popularity_icons ? popularityDict[section.id]?.popularity : undefined
                    "
                    :views="launchDarkly.show_popularity_icons ? (popularityDict[section.id]?.views ?? 0) : 0"
                  />
                </div>
              </div>

              <div
                v-if="myUser && sectionComponentOverrides?.[section.id]?.modified_by?.id === myUser.id"
                class="color-primary3 font-size-[14px] font-600 line-height-[1.2] tracking-[0.4px] text-right self-center text-nowrap"
              >
                <span class="font-size-3 font-400 inline-block mb-1">{{
                  t('canvasUI.canvasBuilder.sectionListRaw.modifiedByYou')
                }}</span
                ><br />
                {{ dayjsInstance(sectionComponentOverrides?.[section.id]?.modified_at).format('MMM D, YYYY h:mm A') }}
              </div>

              <div class="flex items-center flex-shrink-1">
                <CButton
                  v-if="isEditableComputed(section)"
                  v-tooltip="t('canvasUI.canvasBuilder.sectionListRaw.edit')"
                  circle
                  secondary
                  @click="emit('open-section', section, index)"
                >
                  <template #icon>
                    <CIcon fixed-width icon="pencil" size="12px" />
                  </template>
                </CButton>

                <CButton
                  v-else
                  v-tooltip="t('canvasUI.canvasBuilder.sectionListRaw.view')"
                  circle
                  secondary
                  @click="emit('open-section', section, index)"
                >
                  <template #icon>
                    <CIcon fixed-width icon="eye" size="12px" />
                  </template>
                </CButton>
              </div>
            </div>
          </DynamicScrollerItem>
        </template>
      </DynamicScroller>

      <div v-else class="flex flex-col items-center justify-center my-6 gap-4">
        <CIcon class="color-primary4" fa-type="fa" fixed-width icon="folder-open" :size="36" />

        <div class="flex flex-col gap-4 text-center">
          <div class="color-primary3 font-size-[14px] line-height-[20px] tracking-[.25px]">
            {{ t('canvasUI.canvasBuilder.sectionListRaw.empty') }}
          </div>

          <CButton
            :color="themeVars.primary"
            ghost
            :text-color="themeVars.primary"
            type="primary"
            @click="resetFilters"
          >
            {{ t('canvasUI.canvasBuilder.sectionListRaw.filters.reset') }}
          </CButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="tsx">
import { DynamicScroller, DynamicScrollerItem } from 'vue-virtual-scroller'
import { ref, computed, type ComputedRef, inject, onMounted, type Ref } from 'vue'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types.ts'
import type { ComponentNode, SectionListProps } from '@canvas-builder/types/canvas.ts'
import type { CanvasRetrieve, User } from '@openapi'
import CButton from '@lib/components/CButton/CButton.vue'
import CPopularityAndEngagementExtended from '@lib/components/CPopularityAndEngagementIcons/CPopularityAndEngagementExtended.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import CThumbnail from '@lib/components/CThumbnail/CThumbnail.vue'
import CInfoBadge from '@lib/components/CInfoBadge/CInfoBadge.vue'
import useThemeVars from '@lib/composables/useThemeVars.ts'
import { useI18n } from 'vue-i18n'
import useSectionPopularity from '@canvas-builder/composables/useSectionPopularity.ts'
import useRecommendations from '@canvas-builder/composables/useRecommendations.ts'
// for Dynamic and Static Indicators
// import CDynamicIndicator from '@lib/components/CDynamicIndicator/CDynamicIndicator.vue'
import type { OrganizationSettings } from '@lib/types/organizationSettings.types.ts'
import type { InstanceSettings } from '@lib/types/instanceSettings.types.ts'
import { EntityType, usePopularity } from '@canvas-builder/composables/usePopularity.ts'
import { someNodeInTree } from '@canvas-builder/util/tree.ts'
import Search from '@canvas-builder/components/container/Search/Search.vue'
import CPopover from '@lib/components/CPopover/CPopover.vue'
import CMetaDataBadge from '@lib/components/CMetaDataBadge/CMetaDataBadge.vue'
import { NRadio, NRadioGroup, NButton } from 'naive-ui'
import useMetadataTemplates from '@lib/composables/useMetadataTemplates.ts'
import useCanvas from '@canvas-builder/composables/useCanvas.ts'
import dayjsInstance from 'dayjs'

const props = defineProps<{
  name?: string
  style?: Record<string, any>
  sections: Array<{ section: CanvasRetrieve['sections'][number]; index: number }>
  hasSectionSelectorApp?: boolean
}>()

const emit = defineEmits<{
  'select-sections': []
  'open-section': [section: CanvasRetrieve['sections'][number], index: number]
}>()

const themeVars = useThemeVars()
const { t } = useI18n()
const { sectionComponentOverrides, isAdmin } = useCanvas()
const { popularityDict } = useSectionPopularity(computed(() => ({})))
const { recommendedSectionIds } = useRecommendations()
const { fetchPopularityData } = usePopularity()
const search = ref('')
const editabilityFilter = ref<'all' | 'editable' | 'noEditable'>('all')

const myUser = inject<Ref<User | null>>(
  'myUser',
  computed(() => null),
)

const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)

const pitcherSettings = inject<ComputedRef<OrganizationSettings & InstanceSettings>>(
  'pitcherSettings',
  computed(() => ({})),
)

const filteredSections = computed(() => {
  let results = []

  if (editabilityFilter.value === 'editable') {
    results = props.sections.filter((item) => isEditable(item.section))
  } else if (editabilityFilter.value === 'noEditable') {
    results = props.sections.filter((item) => !isEditable(item.section))
  } else {
    results = props.sections
  }

  if (!search.value) return results

  return results.filter((item) => {
    const sectionName = item.section.name?.toLowerCase() || ''
    return sectionName.includes(search.value.toLowerCase())
  })
})

const radioTheme = computed(() => (value: string) => ({
  fontSizeMedium: '16px',
  labelFontWeight: value === editabilityFilter.value ? '600' : '400',
  textColor: 'var(--p-text)',
}))

const activeFilterName = computed(() => {
  if (editabilityFilter.value === 'all') return t('canvasUI.canvasBuilder.sectionListRaw.filters.all')
  if (editabilityFilter.value === 'editable') return t('canvasUI.canvasBuilder.sectionListRaw.filters.editable')
  if (editabilityFilter.value === 'noEditable') return t('canvasUI.canvasBuilder.sectionListRaw.filters.noEditable')

  return ''
})

const metadataMap = computed(() => {
  const skipKeys = pitcherSettings.value?.skip_metadata_keys_in_list_item || []
  const map = new Map()

  props.sections.forEach(({ section }) => {
    const metadata = processItemMetadata(section)
    const filtered = Object.fromEntries(Object.entries(metadata).filter(([key]) => !skipKeys.includes(key)))

    map.set(section.id, filtered)
  })

  return map
})

const { processItemMetadata, fetchTemplate } = useMetadataTemplates()
const selectSections = () => emit('select-sections')

const isEditable = (section: CanvasRetrieve['sections'][number]) =>
  section?.metadata?.pitcher_is_editable_by_rep ||
  someNodeInTree(section?.content?.data || [], (_section: ComponentNode<SectionListProps>): boolean =>
    Boolean(_section?.when_used_in_section?.is_editable || _section?.when_used_in_section?.is_removable),
  )

const isEditableComputed = computed(() => isEditable)

const resetFilters = () => {
  search.value = ''
  editabilityFilter.value = 'all'
}

onMounted(() => {
  if (launchDarkly.value.show_popularity_icons) {
    fetchPopularityData(EntityType.SECTION)
  }

  fetchTemplate()
})
</script>
